import React, { useState, useEffect } from 'react';
import api from '../services/api';

/**
 * Section "Mon Abonnement" - dashboard client.
 * Affiche plan actuel, usage du mois, statut essai, boutons Stripe.
 */
const SectionAbonnement = ({ clientNom }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showResilierConfirm, setShowResilierConfirm] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [plans, setPlans] = useState([]);

  const loadAbonnement = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await api.get('/abonnements/me');
      if (resp && resp.plan) {
        setData(resp);
      } else if (resp && resp.error) {
        setError(resp.error);
      }
      
      const plansResp = await api.get('/abonnements/plans');
      if (plansResp && plansResp.plans) {
        setPlans(plansResp.plans.filter(p => p.id !== 'gratuit'));
      }
    } catch (err) {
      console.error('Erreur chargement abonnements/plans:', err);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadAbonnement();
  }, []);

  // Ouvre une session Stripe Checkout pour upgrade
  const handleUpgrade = async (planId) => {
    setActionLoading('upgrade');
    const resp = await api.post('/abonnements/checkout', { plan_id: planId, cycle: 'mensuel' });
    if (resp && resp.checkout_url) {
      window.location.href = resp.checkout_url;
    } else if (resp && resp.message && resp.plan_id) {
      // Plan gratuit activé directement
      alert('✅ ' + resp.message);
      loadAbonnement();
    } else {
      alert('❌ ' + ((resp && resp.detail) || 'Erreur lors de la création de la session'));
    }
    setActionLoading(null);
  };

  // Ouvre le portal client Stripe (gérer carte, factures, etc.)
  const handlePortal = async () => {
    setActionLoading('portal');
    const resp = await api.post('/abonnements/portal', {});
    if (resp && resp.portal_url) {
      window.location.href = resp.portal_url;
    } else {
      const msg = (resp && resp.detail) || 'Portal Stripe non disponible (clé STRIPE_SECRET_KEY non configurée)';
      alert('⚠️ ' + msg);
    }
    setActionLoading(null);
  };

  // Résilier → bascule plan gratuit
  const handleResilier = async () => {
    setActionLoading('resilier');
    setShowResilierConfirm(false);
    const resp = await api.post('/abonnements/resilier', {});
    if (resp && resp.message) {
      alert('✅ ' + resp.message);
      loadAbonnement();
    } else {
      alert('❌ ' + ((resp && resp.detail) || 'Erreur lors de la résiliation'));
    }
    setActionLoading(null);
  };

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center', color: '#73726C' }}>Chargement de votre abonnement...</div>;
  }

  if (error) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{ color: '#D85A30', marginBottom: '16px' }}>⚠️ {error}</div>
        <button onClick={loadAbonnement} style={btnSecondary}>Réessayer</button>
      </div>
    );
  }

  if (!data) return null;

  const { plan, abonnement, usage_mois, features } = data;

  // Format des compteurs
  const getLimitDisplay = (feature) => {
    const cfg = features[feature];
    if (!cfg) return { used: 0, max: 0, label: 'Indisponible', color: '#999' };
    if (cfg === false) return { used: 0, max: 0, label: 'Indisponible', color: '#999' };
    if (cfg === true || cfg.limite === -1) return { used: usage_mois[feature] || 0, max: -1, label: 'Illimité', color: '#1D9E75' };
    return { used: usage_mois[feature] || 0, max: cfg.limite, label: `${cfg.limite}/mois`, color: '#2D2D2D' };
  };

  const featuresBarres = [
    { key: 'ocr', label: '📄 OCR Documents', ...getLimitDisplay('ocr') },
    { key: 'chat', label: '💬 Chat IA', ...getLimitDisplay('chat') },
    { key: 'documents', label: '📑 Documents', ...getLimitDisplay('documents') },
    { key: 'fiches_paie', label: '💼 Fiches de paie', ...getLimitDisplay('fiches_paie') },
  ];

  // Couleur selon statut
  const statutColor = {
    essai: '#7C3AED',
    actif: '#1D9E75',
    suspendu: '#D85A30',
    impaye: '#D85A30',
    resilie: '#73726C',
  }[abonnement.statut] || '#73726C';

  const statutLabel = {
    essai: 'Période d\'essai',
    actif: 'Actif',
    suspendu: 'Suspendu',
    impaye: 'Paiement en attente',
    resilie: 'Résilié',
  }[abonnement.statut] || abonnement.statut;

  // Jours restants essai
  const joursRestants = abonnement.jours_restants_essai;
  const totalJoursEssai = 30;
  const progressionEssai = joursRestants != null ? ((totalJoursEssai - joursRestants) / totalJoursEssai) * 100 : 0;

  return (
    <div style={{ padding: '24px', maxWidth: '900px', margin: '0 auto' }}>
      <h2 style={{ fontSize: '20px', fontWeight: '600', margin: '0 0 24px' }}>
        💳 Mon Abonnement
      </h2>

      {/* Carte plan actuel */}
      <div
        style={{
          background: 'white',
          border: '1px solid #E8E7E3',
          borderRadius: '16px',
          padding: '24px',
          marginBottom: '20px',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <div style={{ fontSize: '12px', color: '#73726C', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>
              Plan actuel
            </div>
            <div style={{ fontSize: '28px', fontWeight: '600', color: '#2D2D2D' }}>
              {plan.nom}
            </div>
            <div style={{ fontSize: '14px', color: '#73726C', marginTop: '4px' }}>
              {plan.prix_mensuel === 0 ? 'Gratuit à vie' : `${plan.prix_mensuel.toFixed(2).replace('.', ',')}€ / mois · ${abonnement.cycle}`}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{
              display: 'inline-block',
              padding: '6px 14px',
              borderRadius: '20px',
              background: statutColor + '20',
              color: statutColor,
              fontSize: '12px',
              fontWeight: '600',
              marginBottom: '8px',
            }}>
              ● {statutLabel}
            </div>
            {abonnement.date_fin_essai && (
              <div style={{ fontSize: '12px', color: '#73726C' }}>
                Fin essai : {new Date(abonnement.date_fin_essai).toLocaleDateString('fr-FR')}
              </div>
            )}
          </div>
        </div>

        {/* Barre progression essai */}
        {joursRestants != null && (
          <div style={{ marginTop: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#73726C', marginBottom: '6px' }}>
              <span>Progression de l'essai</span>
              <span><strong>{joursRestants} jours restants</strong></span>
            </div>
            <div style={{ background: '#F5F4ED', borderRadius: '8px', height: '8px', overflow: 'hidden' }}>
              <div
                style={{
                  background: '#7C3AED',
                  height: '100%',
                  width: `${progressionEssai}%`,
                  transition: 'width 0.3s',
                }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Usage du mois */}
      <div
        style={{
          background: 'white',
          border: '1px solid #E8E7E3',
          borderRadius: '16px',
          padding: '24px',
          marginBottom: '20px',
        }}
      >
        <h3 style={{ fontSize: '14px', fontWeight: '600', margin: '0 0 16px', color: '#2D2D2D' }}>
          📊 Consommation du mois
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '14px' }}>
          {featuresBarres.map((f) => {
            const pct = f.max === -1 ? Math.min(100, (f.used / 50) * 100) : (f.max > 0 ? (f.used / f.max) * 100 : 0);
            const depasse = f.max > 0 && f.used >= f.max;
            return (
              <div key={f.key}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '4px' }}>
                  <span>{f.label}</span>
                  <span style={{ color: depasse ? '#D85A30' : '#73726C', fontWeight: '500' }}>
                    {f.used}{f.max === -1 ? '' : `/${f.max}`}
                  </span>
                </div>
                <div style={{ background: '#F5F4ED', borderRadius: '6px', height: '6px', overflow: 'hidden' }}>
                  <div
                    style={{
                      background: depasse ? '#D85A30' : (f.max === -1 ? '#1D9E75' : '#0F6E56'),
                      height: '100%',
                      width: `${pct}%`,
                      transition: 'width 0.3s',
                    }}
                  />
                </div>
                <div style={{ fontSize: '11px', color: '#73726C', marginTop: '2px' }}>{f.label.includes('OCR') ? 'Limite' : 'Limite'} : {f.label.includes('OCR') ? f.label : ''}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Actions */}
      <div
        style={{
          background: 'white',
          border: '1px solid #E8E7E3',
          borderRadius: '16px',
          padding: '24px',
        }}
      >
        <h3 style={{ fontSize: '14px', fontWeight: '600', margin: '0 0 16px', color: '#2D2D2D' }}>
          ⚙️ Gérer mon abonnement
        </h3>

        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          {(plan.id === 'gratuit' || !abonnement?.stripe_customer_id) ? (
            <div style={{ width: '100%' }}>
              {!abonnement?.stripe_customer_id && plan.id !== 'gratuit' && (
                <div style={{ background: '#E0F2FE', color: '#0369A1', padding: '12px 16px', borderRadius: '8px', fontSize: '13px', marginBottom: '16px', fontWeight: '500', lineHeight: '1.4' }}>
                  💡 Vous êtes actuellement en période d'essai gratuite du forfait <strong>{plan.nom}</strong>. 
                  Pour activer définitivement votre abonnement ou renseigner vos coordonnées bancaires, veuillez valider une formule ci-dessous.
                </div>
              )}
              <p style={{ fontSize: '13px', color: '#4B5563', marginBottom: '16px' }}>
                Choisissez l'une de nos formules professionnelles pour débloquer toutes les capacités de votre assistant comptable :
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', width: '100%' }}>
                {plans.map(p => (
                  <div key={p.id} style={{ 
                    border: '1px solid #E8E7E3', borderRadius: '12px', padding: '16px', 
                    display: 'flex', flexDirection: 'column', justifyContent: 'space-between', 
                    background: p.id === 'pro' ? '#F6FAF8' : 'white',
                    borderColor: p.id === 'pro' ? '#1D9E75' : '#E8E7E3'
                  }}>
                    <div>
                      <h4 style={{ margin: '0 0 4px', fontSize: '15px', fontWeight: '600', color: '#2D2D2D' }}>{p.nom}</h4>
                      <p style={{ margin: '0 0 12px', fontSize: '11px', color: '#73726C', minHeight: '28px' }}>{p.description}</p>
                      <div style={{ marginBottom: '16px' }}>
                        <span style={{ fontSize: '20px', fontWeight: '700', color: '#2D2D2D' }}>{p.prix_mensuel.toFixed(2).replace('.', ',')}€</span>
                        <span style={{ fontSize: '11px', color: '#73726C' }}> / mois</span>
                      </div>
                    </div>
                    <button
                      onClick={() => handleUpgrade(p.id)}
                      disabled={actionLoading !== null}
                      style={{
                        padding: '8px 14px',
                        background: p.id === 'pro' ? '#1D9E75' : 'white',
                        color: p.id === 'pro' ? 'white' : '#1D9E75',
                        border: p.id === 'pro' ? 'none' : '1px solid #1D9E75',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        width: '100%',
                        transition: 'all 0.15s ease'
                      }}
                    >
                      {actionLoading === 'upgrade' ? '⏳...' : `Souscrire ${p.nom}`}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <>
              <button
                onClick={handlePortal}
                disabled={actionLoading !== null}
                style={btnPrimary}
              >
                {actionLoading === 'portal' ? '⏳ Redirection...' : '💳 Gérer le paiement (Stripe)'}
              </button>
              <button
                onClick={() => setShowResilierConfirm(true)}
                disabled={actionLoading !== null}
                style={btnDanger}
              >
                ❌ Résilier (retour au gratuit)
              </button>
            </>
          )}
        </div>

        <p style={{ fontSize: '11px', color: '#73726C', marginTop: '12px', lineHeight: '1.5' }}>
          💡 La résiliation prend effet immédiatement. Vous conservez l'accès au plan Gratuit.
          Pour changer de plan (upgrade/downgrade), utilisez le portail Stripe.
        </p>
      </div>

      {/* Modal confirmation résiliation */}
      {showResilierConfirm && (
        <div
          style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setShowResilierConfirm(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '24px',
              maxWidth: '400px',
              width: '90%',
            }}
          >
            <h3 style={{ margin: '0 0 12px' }}>Confirmer la résiliation ?</h3>
            <p style={{ fontSize: '14px', color: '#73726C', marginBottom: '20px' }}>
              Vous allez perdre l'accès aux fonctionnalités du plan <strong>{plan.nom}</strong> et revenir au plan Gratuit.
              Cette action est immédiate et irréversible.
            </p>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowResilierConfirm(false)} style={btnSecondary}>Annuler</button>
              <button onClick={handleResilier} style={btnDanger}>Confirmer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Styles
const btnPrimary = {
  padding: '10px 20px',
  background: '#1D9E75',
  color: 'white',
  border: 'none',
  borderRadius: '8px',
  fontSize: '14px',
  fontWeight: '500',
  cursor: 'pointer',
};

const btnSecondary = {
  padding: '10px 20px',
  background: 'white',
  color: '#2D2D2D',
  border: '1px solid #E8E7E3',
  borderRadius: '8px',
  fontSize: '14px',
  fontWeight: '500',
  cursor: 'pointer',
};

const btnDanger = {
  padding: '10px 20px',
  background: 'white',
  color: '#D85A30',
  border: '1px solid #D85A30',
  borderRadius: '8px',
  fontSize: '14px',
  fontWeight: '500',
  cursor: 'pointer',
};

export default SectionAbonnement;
